// src/utils/offlineStorage.ts
// Offline storage using IndexedDB for pending profile changes

const DB_NAME = 'metefy-offline';
const DB_VERSION = 2; // Increment version for new store
const STORE_NAME = 'pendingChanges';
const PROFILE_STORE_NAME = 'profileCache';

interface PendingChange {
  id: string;
  userId: string;
  type: 'profile' | 'token' | 'avatar';
  data: any;
  timestamp: number;
  retries: number;
}

let db: IDBDatabase | null = null;

// Initialize IndexedDB
export async function initOfflineStorage(): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      db = request.result;
      resolve();
    };

    request.onupgradeneeded = (event) => {
      const database = (event.target as IDBOpenDBRequest).result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        const store = database.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
        store.createIndex('userId', 'userId', { unique: false });
        store.createIndex('timestamp', 'timestamp', { unique: false });
      }
      if (!database.objectStoreNames.contains(PROFILE_STORE_NAME)) {
        const profileStore = database.createObjectStore(PROFILE_STORE_NAME, { keyPath: 'userId' });
        profileStore.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });
}

// Save pending change
export async function savePendingChange(
  userId: string,
  type: 'profile' | 'token' | 'avatar',
  data: any
): Promise<string> {
  if (!db) await initOfflineStorage();

  return new Promise((resolve, reject) => {
    const transaction = db!.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    const change: PendingChange = {
      id: `${Date.now()}-${Math.random()}`,
      userId,
      type,
      data,
      timestamp: Date.now(),
      retries: 0,
    };

    const request = store.add(change);
    request.onsuccess = () => resolve(change.id);
    request.onerror = () => reject(request.error);
  });
}

// Get all pending changes
export async function getPendingChanges(userId: string): Promise<PendingChange[]> {
  if (!db) await initOfflineStorage();

  return new Promise((resolve, reject) => {
    const transaction = db!.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('userId');
    const request = index.getAll(userId);

    request.onsuccess = () => {
      const changes = request.result.sort((a, b) => a.timestamp - b.timestamp);
      resolve(changes);
    };
    request.onerror = () => reject(request.error);
  });
}

// Remove pending change after successful sync
export async function removePendingChange(id: string): Promise<void> {
  if (!db) await initOfflineStorage();

  return new Promise((resolve, reject) => {
    const transaction = db!.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(id);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

// Update retry count
export async function updateRetryCount(id: string, retries: number): Promise<void> {
  if (!db) await initOfflineStorage();

  return new Promise((resolve, reject) => {
    const transaction = db!.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const change = getRequest.result;
      if (change) {
        change.retries = retries;
        const putRequest = store.put(change);
        putRequest.onsuccess = () => resolve();
        putRequest.onerror = () => reject(putRequest.error);
      } else {
        resolve();
      }
    };
    getRequest.onerror = () => reject(getRequest.error);
  });
}

// Clear all pending changes for a user
export async function clearPendingChanges(userId: string): Promise<void> {
  if (!db) await initOfflineStorage();

  return new Promise((resolve, reject) => {
    const transaction = db!.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('userId');
    const request = index.openCursor(IDBKeyRange.only(userId));

    request.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      } else {
        resolve();
      }
    };
    request.onerror = () => reject(request.error);
  });
}

// Save profile data to cache
export async function saveProfileCache(userId: string, profileData: any, tokens?: any[]): Promise<void> {
  if (!db) await initOfflineStorage();

  return new Promise((resolve, reject) => {
    const transaction = db!.transaction([PROFILE_STORE_NAME], 'readwrite');
    const store = transaction.objectStore(PROFILE_STORE_NAME);
    
    const cacheData = {
      userId,
      profileData,
      tokens: tokens || [],
      timestamp: Date.now(),
    };

    const request = store.put(cacheData);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

// Get cached profile data
export async function getProfileCache(userId: string): Promise<{ profileData: any; tokens: any[] } | null> {
  if (!db) await initOfflineStorage();

  return new Promise((resolve, reject) => {
    const transaction = db!.transaction([PROFILE_STORE_NAME], 'readonly');
    const store = transaction.objectStore(PROFILE_STORE_NAME);
    const request = store.get(userId);

    request.onsuccess = () => {
      const result = request.result;
      if (result) {
        resolve({
          profileData: result.profileData,
          tokens: result.tokens || [],
        });
      } else {
        resolve(null);
      }
    };
    request.onerror = () => reject(request.error);
  });
}

